import { Compass, LinkedinLogo, Plugs } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";

import { LinkedInLoginModal } from "common/components/LinkedInLogin";
import { MenuSearchInput } from "common/components/MenuSearchInput";
import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useCampaignMutations from "../../../../datahooks/useCampaignMutations";

import LicenseIcon from "assets/icons/license.svg?react";
import RentedIcon from "assets/icons/plans/avatar-outline-small.svg?react";

interface MinerAccountSelectorProps {
  size?: "sm" | "md";
  align?: "center" | "end" | "start";
  className?: string;
  onUpdate?: () => void;
}

export default function MinerAccountSelector({
  size = "md",
  align = "end",
  className = null,
  onUpdate = undefined,
}: MinerAccountSelectorProps) {
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const { miner, id: campaignId, type: campaignType } = useCampaignContext();
  const { accounts } = useSelectedWorkspaceContext();
  const { updateCampaign } = useCampaignMutations();

  const minerAccount = accounts.find(({ id }) => id === miner);
  const isMinerLoggedIn = minerAccount?.state === "LoggedIn";
  const isNavigatorCampaign = campaignType === "navigator";
  const shouldShowSearch = accounts.length > 5;
  const shouldShowReconnect = minerAccount && !isMinerLoggedIn;

  const getTriggerLabel = () => {
    if (!minerAccount) return "Select search account";

    return isMinerLoggedIn ? minerAccount.full_name : "Account disconnected";
  };

  const filteredAccounts = accounts.filter(({ full_name, id }) => {
    const isMatchingSearch = full_name
      .toLowerCase()
      .includes(searchInputValue.toLowerCase());

    const isNotCurrentMiner = id !== miner;

    // In this case, "miner" account will always be visible at the top of the dropdown list
    if (shouldShowReconnect) {
      return isMatchingSearch && isNotCurrentMiner;
    }

    return isMatchingSearch;
  });

  async function handleMinerUpdate(newMiner: string) {
    await updateCampaign({
      campaignId,
      updates: { miner: newMiner },
    });

    if (onUpdate) {
      onUpdate();
    }
  }

  const renderReconnectItem = () => {
    if (!shouldShowReconnect) return null;

    return (
      <DropdownMenuItem onClick={() => setIsLoginModalOpen(true)}>
        <div className="relative">
          <ProfileImage size="xs" src={minerAccount.picture_url} />

          {minerAccount.type === "rental" ? (
            <RentedIcon className="absolute -bottom-1 -right-[5px]" />
          ) : (
            <RenderIf condition={!!minerAccount.license}>
              <LicenseIcon className="absolute -bottom-1 -right-[5px]" />
            </RenderIf>
          )}
        </div>
        <span className="line-clamp-1 break-all text-black-400">
          {minerAccount.full_name}
        </span>
        <span className="ml-auto px-1 text-button-xs">Reconnect</span>
      </DropdownMenuItem>
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild id="miner-account-selector">
          <FilterButton
            leftIcon={
              minerAccount && (
                <ProfileImage
                  key={minerAccount.id}
                  size={size === "md" ? "xs" : "2xs"}
                  src={minerAccount.picture_url}
                />
              )
            }
            size={size}
            className={clsx(className, !isMinerLoggedIn && "border-red-400")}
          >
            {getTriggerLabel()}
          </FilterButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-60" align={align}>
          {shouldShowSearch && (
            <MenuSearchInput
              placeholder="Search accounts"
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
            />
          )}

          <DropdownMenuList>
            {renderReconnectItem()}

            {filteredAccounts.map((account) => {
              const {
                id,
                full_name: fullName,
                picture_url: pictureUrl,
                license,
                sales_navigator: isSalesNavigator,
                state,
                premium: isPremium,
                type,
              } = account;

              const isLoggedIn = state === "LoggedIn";
              const isEnabled = isNavigatorCampaign
                ? isSalesNavigator && isLoggedIn
                : isLoggedIn;

              return (
                <DropdownMenuItem
                  key={id}
                  onSelect={() => handleMinerUpdate(id)}
                  disabled={!isEnabled}
                >
                  <div className="relative shrink-0">
                    <ProfileImage size="xs" src={pictureUrl} />

                    {type === "rental" ? (
                      <RentedIcon className="absolute -bottom-1 -right-[5px]" />
                    ) : (
                      <RenderIf condition={!!license}>
                        <LicenseIcon className="absolute -bottom-1 -right-[5px]" />
                      </RenderIf>
                    )}
                  </div>

                  <span className="line-clamp-1 break-all">{fullName}</span>

                  {isLoggedIn ? (
                    <>
                      <RenderIf condition={isPremium}>
                        <LinkedinLogo
                          weight="fill"
                          className="!size-4 fill-yellow-500"
                        />
                      </RenderIf>
                      <RenderIf condition={isSalesNavigator}>
                        <Compass
                          weight="fill"
                          className="!size-4 fill-blue-500"
                        />
                      </RenderIf>
                    </>
                  ) : (
                    <Plugs weight="fill" className="!size-4 fill-black-400" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>

      <RenderIf condition={shouldShowReconnect}>
        <LinkedInLoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          account={minerAccount}
        />
      </RenderIf>
    </>
  );
}
